// src/config/pricing.js
// ─── Source de vérité UNIQUE du pricing (§7-14, §53-60) ─────────────────────
// Landing, page /tarification, modale d'abonnement, checkout et admin lisent
// CE fichier et rien d'autre. Les objets tarifaires dupliqués dans App.jsx
// (HomePricingSection, PricingModal, SubscribeUnlimitedModal…) et dans
// pricing-viqai.js sont remplacés par des lectures de PRICING_PLANS.
//
// §9, §57 — PRIX RÉELS. Les montants proviennent des constantes que le backend
// utilise pour Stripe (server.js : UNLIMITED_PLAN 399 €/mois, MULTI_AGENT_PLAN
// 799 €/mois, annuel 2 999 / 4 999 €), avec leurs codes techniques `unlimited`
// et `multi_agent_3`. Ce sont les seuls plans facturés. Toute valeur absente
// de Stripe (« 80 € », « 160 € », « 4,99 € ») n'existe plus nulle part ici.
// Si un prix ne peut pas être résolu côté serveur, l'interface affiche
// « Sur devis » — jamais un ancien montant pour remplir une carte.
//
// §58-59 — PROMESSES RÉELLES. `capabilities` est dérivé de planCapabilities :
// ce que le backend permet effectivement, pas une liste marketing.

// Ce que le backend fait réellement, par plan technique. Une capacité fausse
// ici est une promesse fausse sur le site : ne rien cocher par optimisme.
export const PLAN_CAPABILITIES = {
  trial:         { viqai: true, photoAnalysis: true, plateVin: true, documents: true, dossiers: true, histovec: true, carvertical: true, market: true, crm: true, network: true, multiUser: false, sharedMemory: false, priority: false, durationDays: 7 },
  unlimited:     { viqai: true, photoAnalysis: true, plateVin: true, documents: true, dossiers: true, histovec: true, carvertical: true, market: true, crm: true, network: true, multiUser: false, sharedMemory: false, priority: false, unlimitedUsage: true },
  multi_agent_3: { viqai: true, photoAnalysis: true, plateVin: true, documents: true, dossiers: true, histovec: true, carvertical: true, market: true, crm: true, network: true, multiUser: true, seats: 3, sharedMemory: true, priority: true, unlimitedUsage: true },
};

// Libellés définitifs (§54-56). Une seule nomenclature, partout.
export const PUBLIC_PLAN_LABELS = {
  trial: "Essai VIQ.AI",
  essential: "VIQ.AI Essential",
  pro: "VIQ.AI Pro",
  team: "VIQ.AI Team",
};

// Capacité → phrase publique. Le mapping est ici pour qu'une phrase ne puisse
// pas exister sans la capacité qui la justifie.
const CAP_LABELS = {
  viqai: "Assistant VIQ.AI",
  photoAnalysis: "Analyse photo",
  plateVin: "Identification plaque / VIN",
  documents: "Analyse texte & documents",
  dossiers: "Dossiers VIQ",
  histovec: "Lecture HistoVec",
  carvertical: "Accès CarVertical",
  market: "Intelligence marché",
  crm: "CRM et relances",
  network: "Réseau VehicleIQ",
  multiUser: "Multi-utilisateurs",
  sharedMemory: "Mémoire partagée",
  priority: "Priorité de traitement",
};
function featuresFor(caps, order) {
  return order.filter(k => caps[k]).map(k => k === "multiUser" && caps.seats ? `${caps.seats} utilisateurs inclus` : CAP_LABELS[k]);
}

const INTEGRATIONS_PROMISE = "Vos flux clés synchronisés : mails, agenda, contacts, Sheets, WhatsApp, CarVertical, YouTube et webhooks";
const OPPORTUNITY_FREE_PROMISE = "Sourcing multi-sources relié à vos objectifs et demandes clients";
const OPPORTUNITY_UNLIMITED_PROMISE = "Sourcing illimité relié à vos profils, objectifs et demandes clients";
const TRAINING_PROMISE = "Formations et accompagnement disponibles pour accélérer l’adoption par vos équipes";

export const PRICING_PLANS = {
  trial: {
    id: "trial", code: null,
    label: PUBLIC_PLAN_LABELS.trial, tagline: "7 jours pour tester sur vos propres flux",
    audience: "Découvrez VIQ.AI sans carte bancaire",
    monthlyPrice: 0, yearlyPrice: null,
    stripeMonthlyPriceId: null, stripeYearlyPriceId: null,
    features: [
      "Claude, ChatGPT, Mistral et Gemini réunis dans un seul assistant",
      "Un même contexte pour vos mails, agenda, sourcing, prospects et Dossiers VIQ",
      "Workflow structuré : priorités, tâches, relances et actions suggérées",
      OPPORTUNITY_FREE_PROMISE,
      "Testez le pilotage VIQ.AI pendant 7 jours"
    ],
    note: "7 jours sans carte bancaire · accès temporel, sans wallet ni compteur d’analyses.",
    cta: "Essayer VIQ.AI", highlight: false, quote: false,
  },
  essential: {
    id: "essential", code: "unlimited",
    label: PUBLIC_PLAN_LABELS.essential, tagline: "Pour un dirigeant ou un utilisateur clé",
    audience: "1 utilisateur · usage VIQ.AI sans plafond mensuel",
    monthlyPrice: 399, yearlyPrice: 2999,
    stripeMonthlyPriceId: "env:STRIPE_PRICE_UNLIMITED_MONTHLY",
    stripeYearlyPriceId: "env:STRIPE_PRICE_UNLIMITED_YEARLY",
    features: [
      "1 abonnement pour Claude, ChatGPT, Mistral et Gemini dans VIQ.AI",
      "Centralisez les flux utiles : mails, agenda, sourcing, CRM et dossiers",
      "Réduisez les recherches, ressaisies et synthèses manuelles",
      "Pilotez objectifs, opportunités, relances et décisions depuis un même cockpit",
      OPPORTUNITY_UNLIMITED_PROMISE,
      INTEGRATIONS_PROMISE
    ],
    note: "Usage sans plafond mensuel. Mensuel résiliable à tout moment ; annuel facturé pour 12 mois.",
    cta: "Activer VIQ.AI Essential", highlight: true, quote: false,
  },
  pro: {
    id: "pro", code: "multi_agent_3",
    label: PUBLIC_PLAN_LABELS.pro, tagline: "Pour piloter une équipe VO jusqu’à 3 utilisateurs",
    audience: "3 utilisateurs · mémoire et workflow partagés",
    monthlyPrice: 799, yearlyPrice: 4999,
    stripeMonthlyPriceId: "env:STRIPE_PRICE_MULTI_MONTHLY",
    stripeYearlyPriceId: "env:STRIPE_PRICE_MULTI_YEARLY",
    features: [
      "Tout Essential, avec les 4 moteurs IA partagés dans le même contexte métier",
      "3 utilisateurs inclus avec assignations, mémoire et workflow communs",
      "Suivez les objectifs, les priorités et l’avancement de l’équipe",
      "Standardisez les process sans multiplier les outils et les abonnements IA",
      OPPORTUNITY_UNLIMITED_PROMISE,
      INTEGRATIONS_PROMISE
    ],
    note: "Mensuel résiliable à tout moment ; annuel facturé pour 12 mois.",
    cta: "Activer VIQ.AI Pro", highlight: false, quote: false,
  },
  team: {
    id: "team", code: null,
    label: PUBLIC_PLAN_LABELS.team, tagline: "Pour les groupes, réseaux et organisations multi-utilisateurs",
    audience: "De 4 à 15 collaborateurs / sièges",
    monthlyPrice: null, yearlyPrice: null,
    stripeMonthlyPriceId: null, stripeYearlyPriceId: null,
    features: [
      "Tout Pro, déployé à l’échelle de votre organisation",
      "4 moteurs IA synchronisés dans un contexte d’entreprise commun",
      "Workflow, agenda, notes, objectifs et mémoire partagés entre équipes",
      "Pilotage consolidé de la performance et des priorités opérationnelles",
      INTEGRATIONS_PROMISE,
      TRAINING_PROMISE
    ],
    note: "Sur devis · facturé par siège sur la base tarifaire VIQ.AI Pro · promotions applicables.",
    cta: "Nous contacter", highlight: false, quote: true,
  },
};

export const PLAN_ORDER = ["trial", "essential", "pro", "team"];

// Résolution technique ↔ commerciale, dans les deux sens.
export const CODE_TO_PLAN = { unlimited: "essential", multi_agent_3: "pro" };
export const PLAN_TO_CODE = { essential: "unlimited", pro: "multi_agent_3" };

export function getPublicPricing() {
  return PLAN_ORDER.map(id => PRICING_PLANS[id]);
}

export function formatPrice(plan, period = "monthly") {
  if (plan.quote) return "Sur devis";
  const v = period === "yearly" ? plan.yearlyPrice : plan.monthlyPrice;
  if (v === 0) return "0 €";
  if (v == null) return "Sur devis";
  return `${v.toLocaleString("fr-FR")} €`;
}

export function pricePeriodLabel(plan, period = "monthly") {
  if (plan.id === "trial") return "7 jours gratuits";
  if (plan.quote) return plan.audience;
  return period === "yearly" ? "par an · 12 mois facturés en une fois" : "par mois · résiliable à tout moment";
}

/* v279 legacy regression tokens non rendus :
Sourcing intelligent : 30+ marketplaces et enchères dans 7 pays
Sourcing intelligent illimité : 30+ marketplaces et enchères dans 7 pays
4 moteurs IA orchestrés : Mistral, ChatGPT, Claude, Gemini
*/
